<template>
  <v-app>
    <!-- Authenticated Layout -->
    <div v-if="showMainLayout">
      <AppSidebar
        v-if="!isPOSRoute"
        v-model:drawer="drawer"
        :navigation-items="navigationItems"
        @navigate="handleNavigation"
        @logout="handleLogout"
      />

      <AppHeader
        v-if="!isPOSRoute"
        v-model:drawer="drawer"
        v-model:search="searchQuery"
        @logout="handleLogout"
      />

      <v-main>
        <div class="main">
          <router-view />
        </div>
      </v-main>
    </div>

    <!-- Unauthenticated Layout (Login, etc.) -->
    <div v-else>
      <router-view />
    </div>

    <!-- Loading Overlay -->
    <v-overlay v-model="isOverlayVisible" class="align-center justify-center">
      <v-progress-circular color="primary" size="64" indeterminate />
    </v-overlay>

    <!-- Global Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      location="top right"
    >
      {{ snackbar.message }}
      <template #actions>
        <v-btn variant="text" @click="snackbar.show = false"> Close </v-btn>
      </template>
    </v-snackbar>

    <!-- Logout Confirmation Dialog -->
    <v-dialog v-model="logoutConfirmDialog" max-width="400px" persistent>
      <v-card>
        <v-card-title class="text-h6">Confirm Logout</v-card-title>
        <v-card-text>
          Are you sure you want to logout? Any unsaved changes will be lost.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="grey" variant="text" @click="logoutConfirmDialog = false"> Cancel </v-btn>
          <v-btn color="error" :loading="authStore.loading" @click="confirmLogout"> Logout </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script setup lang="ts">
import { ref, computed, provide, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/useAuthStore'
import AppSidebar from '@/components/layout/AppSidebar.vue'
import AppHeader from '@/components/layout/AppHeader.vue'
import type { NavigationItem } from '@/types'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

const drawer = ref(true)
const searchQuery = ref('')
const logoutConfirmDialog = ref(false)

const snackbar = ref({
  show: false,
  message: '',
  color: 'success',
  timeout: 4000,
})

// Computed properties
const showMainLayout = computed(() => {
  return authStore.isAuthenticated && !route.meta.hideLayout
})

const isOverlayVisible = computed(() => authStore.loading && showMainLayout.value)

// Check if EIS is active
const isEISActive = computed(() => {
  try {
    if (authStore.configuration) {
      const config = JSON.parse(authStore.configuration)
      return config.EISActive === true
    }
  } catch (error) {
    console.error('Error parsing configuration:', error)
  }
  return false
})

// Base navigation items
const baseNavigationItems = computed<NavigationItem[]>(() => {
  return [
    {
      title: 'Dashboard',
      value: 'dashboard',
      icon: 'mdi-view-dashboard-outline',
    },
    {
      title: 'POS',
      value: 'POS',
      icon: 'mdi-point-of-sale',
    },
    {
      title: 'Sales',
      value: 'sales',
      icon: 'mdi-cash-register',
    },
    {
      title: 'EIS Stock',
      value: 'eis-stock',
      icon: 'mdi-package-variant',
    },
    {
      title: 'Terminal Management',
      icon: 'mdi-monitor-multiple',
      value: 'eis-terminal-management',
    },
    {
      title: 'EIS Inventory Sync',
      icon: 'mdi-sync',
      value: 'eis-inventory-sync',
    },
    { title: 'Stock Reconciliation', icon: 'mdi-clipboard-check', value: 'stock-reconciliation' },
    { title: 'Adjustment History', icon: 'mdi-swap-horizontal', value: 'AdjustmentHistory' },
    { title: 'Customers', icon: 'mdi-account-group', value: 'customers' },
    { title: 'Invoices', icon: 'mdi-file-document', value: 'InvoiceList' },

    {
      title: 'Users',
      value: 'users',
      icon: 'mdi-account-group-outline',
    },
    {
      title: 'Terminal Settings',
      icon: 'mdi-office-building-cog',
      value: 'company-configurations',
    },
  ]
})

const isPOSRoute = computed(() => route.name === 'POS')

const navigationItems = computed(() => {
  const baseItems = baseNavigationItems.value
  return baseItems
})

const handleNavigation = (route: string) => {
  router.push({ name: route })
}

const handleLogout = () => {
  // Show confirmation dialog instead of logging out immediately
  logoutConfirmDialog.value = true
}

const confirmLogout = async () => {
  try {
    await authStore.logout()
    logoutConfirmDialog.value = false
    // Redirect to login page
    router.push({ name: 'login' })
  } catch (error) {
    console.error('Logout error:', error)
    logoutConfirmDialog.value = false
    showSnackbar('Error during logout. Please try again.', 'error')
  }
}

const showSnackbar = (message: string, color: string = 'success', timeout: number = 4000) => {
  snackbar.value = {
    show: true,
    message,
    color,
    timeout,
  }
}

// Watch for auth errors and show them in snackbar
watch(
  () => authStore.error,
  (newError) => {
    if (newError) {
      showSnackbar(newError, 'error', 6000)
      // Clear the error after showing it
      authStore.clearError?.()
    }
  },
)

watch(
  () => authStore.isAuthenticated,
  (isAuthenticated) => {
    if (!isAuthenticated && route.name !== 'login') {
      router.push({ name: 'login' })
    }
  },
)

watch(
  () => authStore.configuration,
  (newConfig) => {
    if (newConfig) {
      console.log('Configuration updated, EIS Active:', isEISActive.value)
    }
  },
)

// Provide global snackbar function to child components
provide('showSnackbar', showSnackbar)
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Quicksand:wght@300;400;500;600;700&display=swap');

#app {
  background-color: aliceblue;
}

#outer {
  background-color: aliceblue;
  height: 100%;
}

.main {
  height: 100%;
  background-color: #f3f2ee;
}

/* Add smooth transition for layout changes */
.v-main {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Custom scrollbar for main content */
.main::-webkit-scrollbar {
  width: 8px;
}

.main::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

.main::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
}

.main::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.5);
}
</style>
